import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
} from "lucide-preact";
import type {
    ComponentChildren,
    JSX,
} from "preact";

export type SortDirection = "asc" | "desc";

export type SortState<TColumn extends string = string> = {
    column: TColumn;
    direction: SortDirection;
};

type SortableTableHeaderProps<TColumn extends string = string> = {
    column: TColumn;
    label: ComponentChildren;
    currentSort: SortState<TColumn> | null;
    onSortChange: (next: SortState<TColumn>) => void;
    initialDirection?: SortDirection;
    align?: "left" | "right" | "center";
    className?: string;
};

const ALIGNMENT_CLASSES: Record<
    NonNullable<SortableTableHeaderProps["align"]>,
    string
> = {
    left: "text-left justify-start",
    right: "text-right justify-end",
    center: "text-center justify-center",
};

export const SortableTableHeader = <TColumn extends string>({
    column,
    label,
    currentSort,
    onSortChange,
    initialDirection = "asc",
    align = "left",
    className = "",
}: SortableTableHeaderProps<TColumn>): JSX.Element => {
    const isActive = currentSort?.column === column;
    const direction = isActive ? currentSort.direction : null;

    const handleClick = (): void => {
        if (!isActive) {
            onSortChange({ column, direction: initialDirection });
            return;
        }
        onSortChange({
            column,
            direction: direction === "asc" ? "desc" : "asc",
        });
    };

    const renderArrow = (): JSX.Element => {
        if (!isActive) {
            return (
                <ArrowUpDown
                    size={12}
                    className="text-text-muted"
                    aria-hidden="true"
                />
            );
        }
        return direction === "asc"
            ? <ArrowUp size={12} className="text-accent" aria-hidden="true" />
            : (
                <ArrowDown
                    size={12}
                    className="text-accent"
                    aria-hidden="true"
                />
            );
    };

    const ariaSort = !isActive
        ? "none"
        : direction === "asc"
        ? "ascending"
        : "descending";

    const alignmentSplit = ALIGNMENT_CLASSES[align].split(" ");
    const textAlignClass = alignmentSplit[0];
    const justifyClass = alignmentSplit[1];

    return (
        <th
            scope="col"
            aria-sort={ariaSort}
            data-sort-column={column}
            className={`${textAlignClass} px-4 py-2 cursor-pointer hover:text-text-primary transition-colors select-none ${className}`}
            onClick={handleClick}
        >
            <span className={`flex items-center gap-1 ${justifyClass}`}>
                {label}
                {renderArrow()}
            </span>
        </th>
    );
};
